<pdq-tabs-group variant="underline" [leftMargin]="'none'" (onChange)="onTabChange($event)">
  @for (tab of tabs; track tab.value) {
    <pdq-tab [label]="tab.label" [value]="tab.value" [active]="tab.value === activeTab"></pdq-tab>
  }
</pdq-tabs-group>

<!-- ===================== SETTINGS ===================== -->
<div class="tab-panel" [style.display]="activeTab === 'settings' ? null : 'none'">
  @if (showEmbedCard) {
    <pdq-card variant="popover" color="white">
      <div class="card">
        <div class="card__header"><pdq-heading element="h5">Add widget to cart</pdq-heading></div>
        <pdq-divider></pdq-divider>
        <div class="card__content">
          <span class="field__label">Paste the tag below into your cart code</span>
          <pdq-textarea
            [control]="embedControl"
            [readonly]="true"
            [showCopyToClipboard]="true"
            [rows]="embedRows"
            [maxRows]="20"
            [maxLength]="1000"></pdq-textarea>
        </div>
      </div>
    </pdq-card>
  }

  <pdq-card variant="popover" color="white">
    <div class="card">
      <div class="card__header">
        <div class="card__title-block">
          <pdq-heading element="h5">Pricing Model</pdq-heading>
          <span class="card__subtitle">Sets the protection rate charged.</span>
        </div>
        <pdq-button
          class="collapse-chevron"
          [class.collapse-chevron--open]="pricingExpanded"
          icon="arrow_down"
          variant="ghost"
          plain="true"
          size="large"
          iconColor="var(--on-surface-default)"
          (onClick)="togglePricing()"></pdq-button>
      </div>
      @if (pricingExpanded) {
        <pdq-divider></pdq-divider>
        <div class="card__content">
          <div class="field-row">
            <div class="field">
              <span class="field__label">Pricing Type</span>
              <pdq-dropdown
                [formControl]="controls.pricingMethod"
                [options]="pricingTypeOptions"
                fullWidth="true"></pdq-dropdown>
            </div>
            @if (!isPercent) {
              <pdq-input-arrows
                [formControl]="controls.flatPrice"
                label="Protection rate"
                symbol="$"
                [precision]="2"
                [min]="0"
                [step]="0.5"
                widthSize="fullwidth"></pdq-input-arrows>
            } @else {
              <pdq-input-arrows
                [formControl]="controls.percent.controls.cartPercentage"
                label="Percentage from cart total"
                symbol="%"
                [precision]="0"
                [min]="1"
                [max]="100"
                [step]="1"
                widthSize="fullwidth"></pdq-input-arrows>
            }
          </div>
        </div>
      }
    </div>
  </pdq-card>

  <smart-cart-form-custom-code
    [enabledControl]="controls.customCode.controls.enabled"
    [codeControl]="controls.customCode.controls.code"
    [defaultCode]="customCodeStarterTemplate"
    [variables]="customCodeVariables"></smart-cart-form-custom-code>
</div>

<!-- ===================== APPEARANCE ===================== -->
<div class="tab-panel" [style.display]="activeTab === 'appearance' ? null : 'none'">
  <pdq-card variant="popover" color="white">
    <div class="card">
      <div class="card__header"><pdq-heading element="h5">Appearance</pdq-heading></div>
      <pdq-divider></pdq-divider>
      <div class="card__content">
        <div class="field">
          <span class="field__label">Display title*</span>
          <pdq-input
            [formControl]="controls.title"
            placeholder="Protect your order"
            [invalid]="controls.title.errors?.['required']"
            invalidMsg="Add a title — it's used as the product name"></pdq-input>
        </div>

        <div class="field">
          <span class="field__label">Display subtitle</span>
          <pdq-input
            [formControl]="controls.subtitle"
            placeholder="Coverage against loss, theft, or damage in transit."></pdq-input>
        </div>

        <div class="field">
          <pdq-input-color [formControl]="controls.subtitleColor" label="Subtitle Color"></pdq-input-color>
        </div>

        <div class="field">
          <span class="field__label">Upload Product image</span>
          <pdq-image-upload-card
            variant="upload-area"
            thumbnailFit="contain"
            [imageUrl]="productImageUrl"
            [allowedImageTypes]="allowedProductImageTypes"
            [maxFileSizeBytes]="maxProductImageBytes"
            idleTitle="Drop your image file here"
            idleLinkText="or click to browse"
            formatHint="PNG, SVG, JPG"
            [validationErrorMessage]="imageValidationError"
            (onImageChange)="onProductImageChanged($event)"
            (onValidationFailure)="onProductImageValidationFailed($event)"></pdq-image-upload-card>
        </div>

        <pdq-divider></pdq-divider>

        <!-- DESIGN (card) — border around the whole widget card. -->
        <div class="subsection">
          <div class="subsection__header">
            <span class="subsection__title">DESIGN</span>
            <pdq-button
              class="collapse-chevron"
              [class.collapse-chevron--open]="cardDesignExpanded"
              icon="arrow_down"
              variant="ghost"
              plain="true"
              size="large"
              iconColor="var(--on-surface-default)"
              (onClick)="toggleCardDesign()"></pdq-button>
          </div>
          @if (cardDesignExpanded) {
            <div class="subsection__content">
              <pdq-checkbox
                [formControl]="controls.border.controls.enabled"
                label="Border"
                labelSize="small"></pdq-checkbox>
              @if (controls.border.controls.enabled.value) {
                <div class="appearance-row">
                  <pdq-input-arrows
                    [formControl]="controls.border.controls.width"
                    label="Border width"
                    [min]="0"></pdq-input-arrows>
                  <pdq-input-color
                    [formControl]="controls.border.controls.color"
                    label="Border Color"></pdq-input-color>
                </div>
                <pdq-input-arrows
                  [formControl]="controls.border.controls.radius"
                  label="Border Radius"
                  [min]="0"></pdq-input-arrows>
              }
            </div>
          }
        </div>

        <pdq-divider></pdq-divider>

        <!-- Selecting method — radios first, then the selected option's config below (normal flow). -->
        <pdq-radio-group
          [formControl]="controls.selectingMethod.controls.type"
          label="Selecting method"
          [preserveTextCase]="true"
          alignment="vertical">
          <pdq-radio-button value="toggle" text="Toggle"></pdq-radio-button>
          <pdq-radio-button value="checkbox" text="Checkbox"></pdq-radio-button>
          <pdq-radio-button value="quick_checkout" text="Quick checkout button">
            <ng-template #afterContent>
              <span class="select-note select-note--indent"
                >Adds the upsell to the cart and takes the customer straight to checkout.</span
              >
            </ng-template>
          </pdq-radio-button>
        </pdq-radio-group>

        <!-- The selected option's config renders here rather than inside each radio's #afterContent
       template: a projected TemplateRef is re-created whenever its identity changes, which rebuilt
       every rich-textarea (and its Quill instance) on each change-detection pass. -->
        @if (selectingType === 'toggle') {
          <div class="radio-sub">
            <ng-container
              *ngTemplateOutlet="
                selectingColors;
                context: { $implicit: controls.selectingMethod.controls.toggle }
              "></ng-container>
          </div>
        }
        @if (selectingType === 'checkbox') {
          <div class="radio-sub">
            <ng-container
              *ngTemplateOutlet="
                selectingColors;
                context: { $implicit: controls.selectingMethod.controls.checkbox }
              "></ng-container>
          </div>
        }
        @if (isQuickCheckout) {
          <div class="radio-sub">
            <!-- Quick checkout button config -->
            <div class="field">
              <span class="field__label">Button Label</span>
              <pdq-input
                [formControl]="controls.quickCheckout.controls.buttonLabel"
                placeholder="Checkout →"></pdq-input>
            </div>

            <div class="field">
              <span class="field__label">Loader Label</span>
              <pdq-input
                [formControl]="controls.quickCheckout.controls.loaderLabel"
                placeholder="Checking Out..."></pdq-input>
              <span class="field__hint">The text that will be displayed while the customer is being redirected</span>
            </div>

            <div class="field">
              <span class="field__label">Routing</span>
              <pdq-dropdown
                [formControl]="controls.quickCheckout.controls.routing"
                [options]="routingOptions"
                fullWidth="true"></pdq-dropdown>
              <span class="field__hint">Where the checkout button will take the customer when they click it</span>
            </div>
            @if (controls.quickCheckout.controls.routing.value === 'custom_url') {
              <div class="field">
                <span class="field__label">Custom Checkout URL</span>
                <pdq-input
                  [formControl]="controls.quickCheckout.controls.customUrl"
                  [invalid]="controls.quickCheckout.controls.customUrl.invalid"
                  invalidMsg="Enter a full checkout URL (https://… or a /path)"
                  placeholder="https://example.com/checkout"></pdq-input>
              </div>
            }

            <!-- Button DESIGN, 'Continue without', and its DESIGN share a tight gap-8 rhythm. -->
            <div class="quick-design-group">
              <!-- DESIGN (button) -->
              <div class="subsection">
                <div class="subsection__header">
                  <span class="subsection__title">DESIGN</span>
                  <pdq-button
                    class="collapse-chevron"
                    [class.collapse-chevron--open]="designExpanded"
                    icon="arrow_down"
                    variant="ghost"
                    plain="true"
                    size="large"
                    iconColor="var(--on-surface-default)"
                    (onClick)="toggleDesign()"></pdq-button>
                </div>
                @if (designExpanded) {
                  <div class="subsection__content">
                    <div class="field">
                      <span class="field__label">Icon</span>
                      <pdq-iconpicker
                        variant="inline"
                        [presetIconUrls]="iconUrls"
                        [presetIconLabels]="iconLabels"
                        [iconUrl]="controls.quickCheckout.controls.icon.value"
                        [customIconUrl]="controls.quickCheckout.controls.customIcon.value"
                        (iconUrlChange)="onQuickCheckoutIcon($event)"
                        (uploadCustomRequested)="onUploadQuickCheckoutIcon()"
                        (removeCustomRequested)="onRemoveQuickCheckoutIcon()"></pdq-iconpicker>
                    </div>
                    <div class="appearance-row">
                      <pdq-input-color
                        [formControl]="controls.quickCheckout.controls.backgroundColor"
                        label="Background Color"></pdq-input-color>
                      <pdq-input-color
                        [formControl]="controls.quickCheckout.controls.textColor"
                        label="Text Color"></pdq-input-color>
                    </div>
                    <div class="appearance-row">
                      <pdq-input-color
                        [formControl]="controls.quickCheckout.controls.hoverColor"
                        label="Hover Color"></pdq-input-color>
                      <pdq-input-color
                        [formControl]="controls.quickCheckout.controls.iconColor"
                        label="Icon Color"></pdq-input-color>
                    </div>
                    <pdq-input-arrows
                      [formControl]="controls.quickCheckout.controls.cornerRadius"
                      label="Corner Radius"
                      [precision]="2"
                      [min]="0"
                      widthSize="xxlarge"></pdq-input-arrows>
                  </div>
                }
              </div>

              <!-- 'Continue without' button -->
              <div class="field">
                <span class="field__label">'Continue without' button</span>
                <rich-textarea
                  [initialContent]="continueWithoutInitial"
                  (contentChange)="onContinueWithoutChange($event)"
                  [enableLinkInsert]="true"
                  placeholder="Continue without package protection"></rich-textarea>
              </div>
              <div class="subsection">
                <div class="subsection__header">
                  <span class="subsection__title">DESIGN</span>
                  <pdq-button
                    class="collapse-chevron"
                    [class.collapse-chevron--open]="continueDesignExpanded"
                    icon="arrow_down"
                    variant="ghost"
                    plain="true"
                    size="large"
                    iconColor="var(--on-surface-default)"
                    (onClick)="toggleContinueDesign()"></pdq-button>
                </div>
                @if (continueDesignExpanded) {
                  <div class="subsection__content">
                    <div class="appearance-row">
                      <pdq-input-color
                        [formControl]="controls.quickCheckout.controls.continueWithout.controls.textColor"
                        label="Text Color"></pdq-input-color>
                      <pdq-input-color
                        [formControl]="controls.quickCheckout.controls.continueWithout.controls.hoverColor"
                        label="Hover Color"></pdq-input-color>
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>
        }

        <!-- Default/Selected color + shared auto-select. The color pair is passed in per selector (Toggle
             and Checkbox each keep their own), while Auto-selected is shared. -->
        <ng-template #selectingColors let-colors>
          <div class="field-row">
            <pdq-input-color [formControl]="colors.controls.defaultColor" label="Default Color"></pdq-input-color>
            <pdq-input-color [formControl]="colors.controls.selectedColor" label="Selected Color"></pdq-input-color>
          </div>
          <div class="auto-select">
            <pdq-checkbox
              [formControl]="controls.selectingMethod.controls.autoSelected"
              label="Auto-selected"
              labelSize="small"></pdq-checkbox>
            <span class="select-note select-note--indent">Protection is pre-selected; the shopper can opt out.</span>
          </div>
        </ng-template>

        <pdq-divider></pdq-divider>

        <pdq-radio-group
          [formControl]="controls.info.controls.type"
          label="Info button"
          [preserveTextCase]="true"
          alignment="vertical">
          <pdq-radio-button value="tooltip" text="Tooltip"></pdq-radio-button>
          <pdq-radio-button value="modal" text="Modal"></pdq-radio-button>
        </pdq-radio-group>

        <!-- The selected option's config renders here rather than inside each radio's #afterContent
       template: a projected TemplateRef is re-created whenever its identity changes, which rebuilt
       every rich-textarea (and its Quill instance) on each change-detection pass. -->
        @if (!isModal) {
          <div class="radio-sub">
            <div class="field">
              <rich-textarea
                [initialContent]="tooltipInitial"
                (contentChange)="onTooltipChange($event)"
                [enableLinkInsert]="true"
                placeholder="Rest assured, your package is in good hands!"></rich-textarea>
            </div>
          </div>
        }
        @if (isModal) {
          <div class="radio-sub">
            <!-- Headline / Body / List Item are reorderable sections, each with a drag handle. -->
            <div class="modal-sections" cdkDropList (cdkDropListDropped)="onSectionDrop($event)">
              @for (section of modalSections; track section) {
                <div class="modal-section" cdkDrag>
                  <pdq-drag-and-drop
                    class="modal-section__handle"
                    size="medium"
                    spacing="tight"
                    cdkDragHandle></pdq-drag-and-drop>
                  <div class="modal-section__col">
                    <span class="field__label">{{ sectionLabel(section) }}</span>

                    @switch (section) {
                      @case ('headline') {
                        <rich-textarea
                          [initialContent]="modalHeadlineInitial"
                          (contentChange)="onModalHeadlineChange($event)"
                          [enableLinkInsert]="true"
                          placeholder="Headline"></rich-textarea>
                      }
                      @case ('body') {
                        <rich-textarea
                          [initialContent]="modalBodyInitial"
                          (contentChange)="onModalBodyChange($event)"
                          [enableLinkInsert]="true"
                          placeholder="Body"></rich-textarea>
                      }
                      @case ('list') {
                        <div class="list">
                          @for (item of listItems; track item; let i = $index) {
                            <div class="list-item">
                              <pdq-iconpicker
                                class="list-item__icon"
                                variant="popover"
                                [presetIconUrls]="listIconUrls"
                                [presetIconLabels]="listIconLabels"
                                [iconUrl]="$any(item).controls.icon.value"
                                [customIconUrl]="$any(item).controls.customIcon.value"
                                (iconUrlChange)="setListItemIcon(i, $event)"
                                (uploadCustomRequested)="onUploadListItemIcon(i)"
                                (removeCustomRequested)="onRemoveListItemIcon(i)"></pdq-iconpicker>
                              <div class="list-item__fields">
                                <pdq-input
                                  [formControl]="$any(item).controls.title"
                                  placeholder="Package protection"></pdq-input>
                                <pdq-input
                                  [formControl]="$any(item).controls.description"
                                  placeholder="Rest assured, if your package is lost, stolen, or damaged, we've got you covered."></pdq-input>
                              </div>
                              <pdq-button
                                variant="ghost"
                                icon="trash"
                                iconColor="var(--error-default)"
                                (onClick)="removeListItem(i)"></pdq-button>
                            </div>
                          }
                        </div>
                        <pdq-link label="+ Add list item" size="small" (onClick)="addListItem()"></pdq-link>
                      }
                    }
                  </div>
                </div>
              }
            </div>

            <!-- Fine print at the bottom of the modal (supports Privacy/Terms links). -->
            <div class="field">
              <span class="field__label">Footer (fine print)</span>
              <rich-textarea
                [initialContent]="modalFooterInitial"
                (contentChange)="onModalFooterChange($event)"
                [enableLinkInsert]="true"
                placeholder="By purchasing coverage, you agree to the Privacy Policy and Terms and Conditions."></rich-textarea>
            </div>
          </div>
        }
      </div>
    </div>
  </pdq-card>

  <smart-cart-form-custom-code
    [enabledControl]="controls.customCode.controls.enabled"
    [codeControl]="controls.customCode.controls.code"
    [defaultCode]="customCodeStarterTemplate"
    [variables]="customCodeVariables"></smart-cart-form-custom-code>
</div>

<div class="tab-panel" [style.display]="activeTab === 'display-conditions' ? null : 'none'">
  @if (displayConditionsGroup; as displayConditionsForm) {
    <smart-cart-form-display-conditions [formGroup]="displayConditionsForm"></smart-cart-form-display-conditions>
  }
</div>
